/**
 * La fiche d'un enfant : son modèle, ses règles, et sa traduction vers la base.
 *
 * ## Pourquoi le modèle ne suit pas les tables
 *
 * Le parcours d'origine avait six étapes parce que la base a six tables. Le
 * parent, lui, ne pense pas en tables : « la santé » et « les allergies » sont
 * une seule question pour lui — ce que l'équipe doit savoir pour que son enfant
 * ne soit pas en danger — et elles étaient deux écrans.
 *
 * Le modèle ci-dessous suit donc les étapes telles qu'on les pose, et c'est
 * `versLignes()` qui les redistribue vers `kids`, `kid_health`,
 * `kid_allergies`, `kid_activities`, `kid_departure`, `kid_inclusion` et
 * `kid_image_rights`. Le découpage technique existe toujours ; il a cessé de
 * s'imposer à la famille.
 *
 * ## Les validations vivent ici, pas dans les écrans
 *
 * Un écran valide ce qu'il affiche ; une fiche, elle, doit pouvoir être
 * vérifiée en entier — au récapitulatif, et demain à la vérification
 * périodique avant inscription. Séparer les deux évite que la règle change
 * selon l'endroit d'où on la regarde.
 */

import { chiffresNRN, erreurNRN, lireNRN, prefixeNRN } from '../../../lib/registreNational';
import { erreurClasse, type ClassesConnues } from '../../../lib/classes/regles';

export { prefixeNRN };
import type { Database } from '../../../lib/database.types';
import {
  DROIT_IMAGE_VIDE,
  depuisLigne,
  erreursDroitImage,
  versLigne,
  type DroitImage,
} from '../../../lib/droitImage';

export type NiveauNatation = 'pas du tout' | 'difficilement' | 'bien' | 'très bien';
export type Accompagnement = 'yes' | 'no' | 'sometimes';

/** Les quatre niveaux proposés, dans l'ordre où le formulaire les affiche. */
export const NIVEAUX_NATATION: NiveauNatation[] = [
  'pas du tout',
  'difficilement',
  'bien',
  'très bien',
];

/**
 * Ce que chaque niveau veut dire, dit avec le prénom de l'enfant.
 *
 * « Pas du tout » et « Difficilement » ne répondaient à aucune question
 * énoncée : posés côte à côte sous un intitulé, ils obligeaient à reconstituer
 * mentalement « pas du tout quoi ? ». Une phrase entière — « Léa ne sait pas
 * nager » — se lit et se choisit sans ce détour.
 *
 * Les phrases vivent ici plutôt que dans l'écran, parce que le récapitulatif
 * les affiche aussi : deux formulations pour une même colonne feraient douter
 * le parent d'avoir bien répondu.
 */
export const PHRASE_NATATION: Record<NiveauNatation, (enfant: string) => string> = {
  'pas du tout': (enfant) => `${enfant} ne sait pas nager`,
  difficilement: (enfant) => `${enfant} sait nager difficilement`,
  bien: (enfant) => `${enfant} sait bien nager`,
  'très bien': (enfant) => `${enfant} a un très bon niveau de natation`,
};

export type Etape = 'identite' | 'sante' | 'vie' | 'sorties' | 'autorisations' | 'recapitulatif';

export const ETAPES: { cle: Etape; titre: string; court: string }[] = [
  { cle: 'identite', titre: 'Votre enfant', court: 'Enfant' },
  { cle: 'sante', titre: 'Santé et alimentation', court: 'Santé' },
  { cle: 'vie', titre: 'Sa journée en stage', court: 'Journée' },
  { cle: 'sorties', titre: 'Qui vient le ou la chercher', court: 'Sorties' },
  { cle: 'autorisations', titre: 'Autorisations et vie privée', court: 'Autorisations' },
  { cle: 'recapitulatif', titre: 'Récapitulatif', court: 'Récapitulatif' },
];

export interface Identite {
  prenom: string;
  nom: string;
  dateNaissance: string;
  nNational: string;
  adresse: string;
  cPostal: string;
  localite: string;
  ecole: string;
  /** Rempli seulement quand l'école n'est pas dans la liste. */
  ecoleAutre: string;
  /**
   * L'adresse est celle du parent : elle a été reprise de son profil et les
   * champs sont masqués. Ce n'est qu'un état du formulaire — la base reçoit
   * toujours l'adresse elle-même, l'enfant garde sa copie.
   */
  memeAdresseQueParent: boolean;
  /**
   * La classe de l'année scolaire en cours (`centre_classes.id`), ou
   * `CLASSE_HORS_LISTE` si le parent l'a signalée à l'équipe. Écrite dans
   * `kid_classes` par la page, pas par `versLignes` : une ligne par année.
   */
  classeId: string;
}

export interface Sante {
  infosMedicales: string;
  antecedents: string;
  medication: boolean;
  medicationDetails: string;
  medicationAutonomie: boolean;
  tetanos: boolean;
  medecinNom: string;
  medecinTel: string;
  allergies: boolean;
  allergiesDetails: string;
  allergiesConsequences: string;
  regimeSpecial: boolean;
  regimeDetails: string;
}

export interface VieEnStage {
  participeATout: boolean;
  restrictions: string;
  natation: NiveauNatation | '';
  peurDeLEau: boolean;
  autresInfos: string;
  besoinsSpecifiques: boolean;
  situation: string;
  impact: string;
  accompagnementDedie: Accompagnement | '';
  accompagnementDetails: string;
  strategies: string;
  materiel: string;
  signauxDeStress: string;
  forces: string;
  experiences: string;
}

export interface Sorties {
  partSeul: boolean;
  heureDepart: string;
  personnesIds: string[];
}

export interface Autorisations {
  /** L'accord pour les soins, repris du texte de l'ONE. */
  soins: boolean;
  image: DroitImage;
  politiqueLue: boolean;
}

export interface Fiche {
  identite: Identite;
  sante: Sante;
  vie: VieEnStage;
  sorties: Sorties;
  autorisations: Autorisations;
}

export const FICHE_VIDE: Fiche = {
  identite: {
    prenom: '',
    nom: '',
    dateNaissance: '',
    nNational: '',
    adresse: '',
    cPostal: '',
    localite: '',
    ecole: '',
    ecoleAutre: '',
    memeAdresseQueParent: false,
    classeId: '',
  },
  sante: {
    infosMedicales: '',
    antecedents: '',
    medication: false,
    medicationDetails: '',
    medicationAutonomie: false,
    tetanos: false,
    medecinNom: '',
    medecinTel: '',
    allergies: false,
    allergiesDetails: '',
    allergiesConsequences: '',
    regimeSpecial: false,
    regimeDetails: '',
  },
  vie: {
    participeATout: true,
    restrictions: '',
    /*
      Vide, et non « pas du tout ».

      Le formulaire d'origine partait de « pas du tout » : un parent qui
      traversait l'étape sans y toucher déclarait donc que son enfant ne sait
      pas nager. Écrit dans la base, lu par l'équipe à la piscine — une valeur
      par défaut sur cette question-là est une fausse déclaration silencieuse.
    */
    natation: '',
    peurDeLEau: false,
    autresInfos: '',
    besoinsSpecifiques: false,
    situation: '',
    impact: '',
    accompagnementDedie: '',
    accompagnementDetails: '',
    strategies: '',
    materiel: '',
    signauxDeStress: '',
    forces: '',
    experiences: '',
  },
  sorties: {
    partSeul: false,
    heureDepart: '',
    personnesIds: [],
  },
  autorisations: {
    soins: false,
    image: { ...DROIT_IMAGE_VIDE },
    politiqueLue: false,
  },
};

export type Erreurs = Record<string, string>;

/* -------------------------------------------------------------------------- */
/* Les règles                                                                  */
/* -------------------------------------------------------------------------- */

/** L'adresse d'un profil parent, si elle est complète ; sinon `null`. */
export function adresseDuProfil(
  profil: { adresse?: string | null; cpostal?: string | null; localite?: string | null } | null | undefined,
): { adresse: string; cPostal: string; localite: string } | null {
  const adresse = profil?.adresse?.trim() ?? '';
  const cPostal = profil?.cpostal?.trim() ?? '';
  const localite = profil?.localite?.trim() ?? '';
  return adresse && cPostal && localite ? { adresse, cPostal, localite } : null;
}

/** Deux adresses identiques, à la casse et aux espaces près. */
export function memeAdresse(
  a: { adresse: string; cPostal: string; localite: string },
  b: { adresse: string; cPostal: string; localite: string },
): boolean {
  const n = (t: string) => t.trim().replace(/\s+/g, ' ').toLowerCase();
  return n(a.adresse) === n(b.adresse) && n(a.cPostal) === n(b.cPostal) && n(a.localite) === n(b.localite);
}

/**
 * Le numéro à retenir. Les six chiffres de la date, proposés à l'entrée du
 * champ, ne sont pas un numéro : laissés seuls, ils valent « non renseigné ».
 */
export function nrnRetenu(v: Identite): string {
  const nrn = chiffresNRN(v.nNational);
  return nrn && nrn === prefixeNRN(v.dateNaissance) ? '' : nrn;
}

/**
 * Ce que la fiche sait du reste du compte, et qui change la validation :
 * les numéros de registre national déjà portés par le parent et les autres
 * enfants, qu'une fiche ne peut pas reprendre.
 */
export interface ContexteFiche {
  nrnInterdits?: string[];
  /** Les centres reliés aux écoles et leurs classes : la classe est exigée quand l'école en a. */
  classes?: ClassesConnues;
}

export function erreursIdentite(v: Identite, contexte: ContexteFiche = {}): Erreurs {
  const e: Erreurs = {};
  if (!v.prenom.trim()) e.prenom = 'Le prénom est requis.';
  if (!v.nom.trim()) e.nom = 'Le nom est requis.';

  if (!v.dateNaissance) {
    e.dateNaissance = 'La date de naissance est requise.';
  } else {
    const d = new Date(v.dateNaissance);
    if (Number.isNaN(d.getTime())) {
      e.dateNaissance = 'Cette date n’est pas valable.';
    } else if (d > new Date()) {
      e.dateNaissance = 'La date de naissance est dans le futur.';
    } else if (d < new Date('1990-01-01')) {
      e.dateNaissance = 'Cette date semble erronée.';
    }
  }

  /*
    Le numéro de registre national reste facultatif : sans lui l'enfant est
    accueilli, seule l'attestation fiscale manque. Mais s'il est renseigné, la
    base le refuse quand la clé de contrôle ne tombe pas juste — mieux vaut le
    dire ici que laisser l'enregistrement échouer sur une erreur Postgres.
  */
  const nrn = nrnRetenu(v);
  if (nrn) {
    const probleme = erreurNRN(nrn, {
      qui: 'enfant',
      dateNaissance: e.dateNaissance ? undefined : v.dateNaissance,
      interdits: contexte.nrnInterdits,
    });
    if (probleme) e.nNational = probleme;
  }

  if (!v.adresse.trim()) e.adresse = 'L’adresse est requise.';
  if (!v.cPostal.trim()) e.cPostal = 'Le code postal est requis.';
  else if (!/^\d{4}$/.test(v.cPostal.trim())) e.cPostal = 'Un code postal belge compte 4 chiffres.';
  if (!v.localite.trim()) e.localite = 'La localité est requise.';

  if (!v.ecole) e.ecole = 'L’école est requise.';
  else if (v.ecole === 'other' && !v.ecoleAutre.trim()) {
    e.ecoleAutre = 'Indiquez le nom de l’école.';
  } else {
    const classe = erreurClasse(v.ecole, v.classeId, contexte.classes);
    if (classe) e.classeId = classe;
  }

  return e;
}

export function erreursSante(v: Sante): Erreurs {
  const e: Erreurs = {};

  /*
    Le NOM du médecin est requis, pas son numéro.

    Les deux étaient facultatifs, et une fiche sans médecin est un trou le jour
    où il faut appeler. Mais exiger aussi le téléphone bloquerait des familles
    qui ne l'ont pas sous la main au moment de l'inscription — alors qu'un
    cabinet se retrouve en quelques secondes à partir d'un nom. On demande donc
    ce qui ne se retrouve pas, et on laisse facultatif ce qui se retrouve.
  */
  if (!v.medecinNom.trim()) {
    e.medecinNom = 'Le nom du médecin traitant est requis.';
  }

  if (v.medication && !v.medicationDetails.trim()) {
    e.medicationDetails = 'Précisez le médicament et la posologie.';
  }
  if (v.allergies && !v.allergiesDetails.trim()) {
    e.allergiesDetails = 'Décrivez les allergies.';
  }
  if (v.allergies && !v.allergiesConsequences.trim()) {
    e.allergiesConsequences = 'Décrivez ce qui se passe en cas de contact.';
  }
  if (v.regimeSpecial && !v.regimeDetails.trim()) {
    e.regimeDetails = 'Décrivez le régime à respecter.';
  }
  return e;
}

export function erreursVie(v: VieEnStage): Erreurs {
  const e: Erreurs = {};
  if (!v.participeATout && !v.restrictions.trim()) {
    e.restrictions = 'Précisez ce que votre enfant ne peut pas faire.';
  }
  if (!v.natation) e.natation = 'Indiquez le niveau de natation.';
  if (v.besoinsSpecifiques && !v.situation.trim()) {
    e.situation = 'Décrivez la situation pour que nous puissions nous préparer.';
  }
  return e;
}

export function erreursSorties(v: Sorties): Erreurs {
  const e: Erreurs = {};
  /*
    L'étape d'origine refusait d'avancer sans rien afficher quand aucune des
    deux options n'était renseignée. Le clic ne faisait rien, et le parent
    croyait le site cassé.
  */
  if (v.partSeul) {
    if (!v.heureDepart) e.heureDepart = 'Indiquez l’heure à partir de laquelle il peut partir.';
  } else if (v.personnesIds.length === 0) {
    e.personnesIds =
      'Ajoutez au moins une personne autorisée, ou indiquez que votre enfant peut partir seul.';
  }
  return e;
}

export function erreursAutorisations(v: Autorisations): Erreurs {
  const e: Erreurs = { ...erreursDroitImage(v.image) };
  if (!v.soins) e.soins = 'Cet accord est nécessaire pour accueillir votre enfant.';
  if (!v.politiqueLue) e.politiqueLue = 'Merci de confirmer avoir pris connaissance de la politique.';
  return e;
}

/** Les erreurs d'une étape donnée. */
export function erreursEtape(etape: Etape, fiche: Fiche, contexte: ContexteFiche = {}): Erreurs {
  switch (etape) {
    case 'identite':
      return erreursIdentite(fiche.identite, contexte);
    case 'sante':
      return erreursSante(fiche.sante);
    case 'vie':
      return erreursVie(fiche.vie);
    case 'sorties':
      return erreursSorties(fiche.sorties);
    case 'autorisations':
      return erreursAutorisations(fiche.autorisations);
    default:
      return {};
  }
}

/** La première étape qui bloque, s'il y en a une. */
export function premiereEtapeIncomplete(fiche: Fiche, contexte: ContexteFiche = {}): Etape | null {
  for (const { cle } of ETAPES) {
    if (cle === 'recapitulatif') continue;
    if (Object.keys(erreursEtape(cle, fiche, contexte)).length > 0) return cle;
  }
  return null;
}

/* -------------------------------------------------------------------------- */
/* Vers la base                                                                */
/* -------------------------------------------------------------------------- */

/** L'école telle qu'elle doit être stockée : un identifiant, ou le nom saisi. */
export function ecoleEnregistree(v: Identite): string {
  return v.ecole === 'other' ? v.ecoleAutre.trim() : v.ecole;
}

type Tables = Database['public']['Tables'];

export interface LignesFiche {
  /* `user_id` est ajouté au moment de l'insertion : il vient de la session, pas de la fiche. */
  kids: Omit<Tables['kids']['Insert'], 'user_id'>;
  kid_health: Tables['kid_health']['Insert'];
  kid_allergies: Tables['kid_allergies']['Insert'];
  kid_activities: Tables['kid_activities']['Insert'];
  kid_departure: Tables['kid_departure']['Insert'];
  kid_inclusion: Tables['kid_inclusion']['Insert'];
  kid_image_rights: Tables['kid_image_rights']['Insert'];
}

/**
 * Traduit la fiche vers les sept lignes à écrire.
 *
 * `kidId` est vide à la création : la ligne `kids` est écrite en premier et les
 * six autres reçoivent l'identifiant obtenu.
 */
export function versLignes(
  fiche: Fiche,
  kidId: string,
  auteurId: string | null,
  versionPolitique: string,
): LignesFiche {
  const { identite, sante, vie, sorties, autorisations } = fiche;
  const nrn = nrnRetenu(identite);

  return {
    kids: {
      nom: identite.nom.trim(),
      prenom: identite.prenom.trim(),
      date_naissance: identite.dateNaissance,
      /* Vide devient NULL : la contrainte de la base refuse une chaîne vide. */
      n_national: nrn || null,
      is_national_number_valid: nrn ? lireNRN(nrn).ok : false,
      adresse: identite.adresse.trim(),
      cpostal: identite.cPostal.trim(),
      localite: identite.localite.trim(),
      ecole: ecoleEnregistree(identite),
      last_verified_at: new Date().toISOString(),
    },
    kid_health: {
      kid_id: kidId,
      specific_medical: sante.infosMedicales.trim() || null,
      past_medical: sante.antecedents.trim() || null,
      medication: sante.medication,
      medication_details: sante.medication ? sante.medicationDetails.trim() : null,
      medication_autonomy: sante.medication && sante.medicationAutonomie,
      tetanus: sante.tetanos,
      doctor_name: sante.medecinNom.trim() || null,
      doctor_phone: sante.medecinTel.trim() || null,
      parental_consent: autorisations.soins,
      medication_form_sent: false,
    },
    kid_allergies: {
      kid_id: kidId,
      has_allergies: sante.allergies,
      allergies_details: sante.allergies ? sante.allergiesDetails.trim() : null,
      allergies_consequences: sante.allergies ? sante.allergiesConsequences.trim() : null,
      special_diet: sante.regimeSpecial,
      diet_details: sante.regimeSpecial ? sante.regimeDetails.trim() : null,
    },
    kid_activities: {
      kid_id: kidId,
      can_participate: vie.participeATout,
      restriction_details: vie.participeATout ? null : vie.restrictions.trim(),
      swim_level: vie.natation,
      /*
        `can_swim` est dérivé, pas demandé.

        Les écrans du personnel affichent « Sait nager : oui / non » d'après
        cette colonne, et ne montrent le niveau QUE si elle est vraie. Le
        formulaire des parents ne l'écrivait jamais : un enfant déclaré « très
        bien » apparaissait donc à l'équipe comme ne sachant pas nager, et son
        niveau restait caché. Deux questions pour une seule réalité, c'est une
        de trop — celle-ci se déduit.
      */
      can_swim: vie.natation === 'bien' || vie.natation === 'très bien',
      water_fear: vie.peurDeLEau,
      other_info: vie.autresInfos.trim() || null,
    },
    kid_departure: {
      kid_id: kidId,
      leaves_alone: sorties.partSeul,
      departure_time: sorties.partSeul ? sorties.heureDepart : null,
      pickup_people_ids: sorties.partSeul ? [] : sorties.personnesIds,
    },
    kid_inclusion: {
      kid_id: kidId,
      has_needs: vie.besoinsSpecifiques,
      situation_details: vie.besoinsSpecifiques ? vie.situation.trim() || null : null,
      impact_details: vie.besoinsSpecifiques ? vie.impact.trim() || null : null,
      needs_dedicated_staff: vie.besoinsSpecifiques ? vie.accompagnementDedie || null : null,
      staff_details: vie.besoinsSpecifiques ? vie.accompagnementDetails.trim() || null : null,
      strategies: vie.besoinsSpecifiques ? vie.strategies.trim() || null : null,
      assistive_devices: vie.besoinsSpecifiques ? vie.materiel.trim() || null : null,
      stress_signals: vie.besoinsSpecifiques ? vie.signauxDeStress.trim() || null : null,
      strengths: vie.besoinsSpecifiques ? vie.forces.trim() || null : null,
      previous_experience: vie.besoinsSpecifiques ? vie.experiences.trim() || null : null,
    },
    kid_image_rights: versLigne(autorisations.image, kidId, versionPolitique, auteurId),
  };
}

/* -------------------------------------------------------------------------- */
/* Modifier une étape d'une fiche enregistrée                                  */
/* -------------------------------------------------------------------------- */

type TableFille = 'kid_health' | 'kid_allergies' | 'kid_activities' | 'kid_departure' | 'kid_inclusion' | 'kid_image_rights';

export interface EcrituresDeLEtape {
  /** Les colonnes de `kids` à mettre à jour, s'il y en a. */
  kids: Omit<LignesFiche['kids'], 'last_verified_at'> | null;
  /** Les lignes filles à écrire (`upsert` sur `kid_id`), dans l'ordre. */
  lignes: { table: TableFille; ligne: Record<string, unknown> }[];
}

const pareil = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

/**
 * Ce qu'il faut écrire quand le parent modifie UNE étape de la fiche depuis sa
 * page, et rien de plus.
 *
 * - **Seulement ce qui a changé.** Une table dont la ligne est identique à
 *   celle de l'ouverture n'est pas réécrite : réenregistrer le droit à
 *   l'image sans y toucher le redaterait comme un nouvel accord.
 * - **`last_verified_at` n'est jamais écrit.** Corriger une adresse n'est pas
 *   relire toute la fiche : seule la vérification complète
 *   (`/kids/:id/verifier`) la déclare à jour.
 * - **`medication_form_sent`** repart à `false` seulement si le médicament
 *   change — un nouveau traitement demande un nouveau formulaire, un numéro de
 *   médecin corrigé non.
 * - **L'accord de soins** est posé avec les autorisations mais stocké dans
 *   `kid_health` : l'étape des autorisations réécrit la ligne santé quand il
 *   change, avec les réponses enregistrées.
 */
export function ecrituresDeLEtape(
  etape: Etape,
  fiche: Fiche,
  depart: Fiche,
  kidId: string,
  auteurId: string | null,
  versionPolitique: string,
): EcrituresDeLEtape {
  const apres = versLignes(fiche, kidId, auteurId, versionPolitique);
  const avant = versLignes(depart, kidId, auteurId, versionPolitique);
  const ecritures: EcrituresDeLEtape = { kids: null, lignes: [] };
  const siChange = (table: TableFille, ligne: Record<string, unknown>, ancienne: Record<string, unknown>) => {
    if (!pareil(ligne, ancienne)) ecritures.lignes.push({ table, ligne });
  };

  switch (etape) {
    case 'identite': {
      const { last_verified_at: _a, ...kids } = apres.kids;
      const { last_verified_at: _b, ...anciens } = avant.kids;
      void _a;
      void _b;
      if (!pareil(kids, anciens)) ecritures.kids = kids;
      break;
    }
    case 'sante': {
      const { medication_form_sent: _a, ...sante } = apres.kid_health;
      const { medication_form_sent: _b, ...ancienne } = avant.kid_health;
      void _a;
      void _b;
      if (!pareil(sante, ancienne)) {
        const traitementChange =
          sante.medication !== ancienne.medication || sante.medication_details !== ancienne.medication_details;
        ecritures.lignes.push({
          table: 'kid_health',
          ligne: traitementChange ? { ...sante, medication_form_sent: false } : sante,
        });
      }
      siChange('kid_allergies', apres.kid_allergies, avant.kid_allergies);
      break;
    }
    case 'vie':
      siChange('kid_activities', apres.kid_activities, avant.kid_activities);
      siChange('kid_inclusion', apres.kid_inclusion, avant.kid_inclusion);
      break;
    case 'sorties':
      siChange('kid_departure', apres.kid_departure, avant.kid_departure);
      break;
    case 'autorisations': {
      if (fiche.autorisations.soins !== depart.autorisations.soins) {
        /*
          La ligne santé entière, et pas la seule colonne : une fiche ancienne
          peut ne pas en avoir, et une ligne réduite à l'accord n'aurait pas les
          autres réponses. Elles valent ici ce qui est enregistré.
        */
        const { medication_form_sent: _a, ...sante } = apres.kid_health;
        void _a;
        ecritures.lignes.push({ table: 'kid_health', ligne: sante });
      }
      /*
        Les horodatages et l'auteur changent à chaque appel : on compare les
        réponses. La politique lue n'a pas de colonne — l'existence de la ligne,
        avec sa version, en est la trace : la cocher l'écrit aussi.
      */
      if (
        !pareil(fiche.autorisations.image, depart.autorisations.image) ||
        (fiche.autorisations.politiqueLue && !depart.autorisations.politiqueLue)
      ) {
        ecritures.lignes.push({ table: 'kid_image_rights', ligne: apres.kid_image_rights });
      }
      break;
    }
    default:
      break;
  }
  return ecritures;
}

/* -------------------------------------------------------------------------- */
/* Depuis la base                                                              */
/* -------------------------------------------------------------------------- */

type Lue<T extends keyof Tables> = Partial<Tables[T]['Row']> | null | undefined;

/** Une fiche telle qu'elle revient de la base, sous-tables comprises. */
export interface LignesLues {
  kids: Lue<'kids'>;
  health?: Lue<'kid_health'>;
  allergies?: Lue<'kid_allergies'>;
  activities?: Lue<'kid_activities'>;
  departure?: Lue<'kid_departure'>;
  inclusion?: Lue<'kid_inclusion'>;
  image_rights?: Lue<'kid_image_rights'>;
}

/** Reconnaît un identifiant d'école (un UUID) d'un nom saisi à la main. */
const EST_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Relit une fiche enregistrée vers le modèle du formulaire.
 *
 * C'est l'inverse de `versLignes()`, et il existe pour une seule raison : que
 * la question « cette fiche est-elle complète ? » se pose avec les MÊMES règles
 * à la création et sur une fiche déjà en base. Écrire un second jeu de règles
 * qui lirait directement les colonnes ferait diverger les deux au premier
 * changement — et la divergence se verrait le jour où un parent traverse le
 * formulaire sans rien pouvoir corriger.
 *
 * Toutes les sous-tables sont facultatives : 2 939 fiches sont antérieures à ce
 * parcours et certaines n'ont jamais eu de ligne santé. Une sous-table absente
 * n'est pas une réponse « non », c'est une question jamais posée — et c'est
 * ainsi qu'elle ressort ici, en champ vide que la validation réclamera.
 */
export function depuisLignes(l: LignesLues): Fiche {
  const k = l.kids ?? {};
  const sante = l.health ?? {};
  const allergies = l.allergies ?? {};
  const activites = l.activities ?? {};
  const sorties = l.departure ?? {};
  const inclusion = l.inclusion ?? {};

  const ecole = k.ecole ?? '';
  const ecoleListee = EST_UUID.test(ecole);

  return {
    identite: {
      prenom: k.prenom ?? '',
      nom: k.nom ?? '',
      dateNaissance: k.date_naissance ?? '',
      nNational: k.n_national ?? '',
      adresse: k.adresse ?? '',
      cPostal: k.cpostal ?? '',
      localite: k.localite ?? '',
      /*
        L'ancien formulaire stockait tantôt un identifiant d'école, tantôt le
        nom tapé à la main — et parfois le mot « other » lui-même. On distingue
        les deux à la forme : ce qui ressemble à un UUID est une école listée,
        le reste est un nom.
      */
      ecole: ecole && !ecoleListee ? 'other' : ecole,
      ecoleAutre: ecole && !ecoleListee ? ecole : '',
      memeAdresseQueParent: false,
      /* La classe vient de `kid_classes`, lue à part par la page. */
      classeId: '',
    },
    sante: {
      infosMedicales: sante.specific_medical ?? '',
      antecedents: sante.past_medical ?? '',
      medication: sante.medication ?? false,
      medicationDetails: sante.medication_details ?? '',
      medicationAutonomie: sante.medication_autonomy ?? false,
      tetanos: sante.tetanus ?? false,
      medecinNom: sante.doctor_name ?? '',
      medecinTel: sante.doctor_phone ?? '',
      allergies: allergies.has_allergies ?? false,
      allergiesDetails: allergies.allergies_details ?? '',
      allergiesConsequences: allergies.allergies_consequences ?? '',
      regimeSpecial: allergies.special_diet ?? false,
      regimeDetails: allergies.diet_details ?? '',
    },
    vie: {
      participeATout: activites.can_participate ?? true,
      restrictions: activites.restriction_details ?? '',
      /*
        `swim_level` est une colonne texte libre : on ne reprend que les quatre
        niveaux que le formulaire connaît. Une valeur d'un autre temps devient
        un champ vide, donc une question reposée — plutôt qu'un niveau affiché
        que personne n'a choisi.
      */
      natation: NIVEAUX_NATATION.includes(activites.swim_level as NiveauNatation)
        ? (activites.swim_level as NiveauNatation)
        : '',
      peurDeLEau: activites.water_fear ?? false,
      autresInfos: activites.other_info ?? '',
      besoinsSpecifiques: inclusion.has_needs ?? false,
      situation: inclusion.situation_details ?? '',
      impact: inclusion.impact_details ?? '',
      accompagnementDedie: (inclusion.needs_dedicated_staff as Accompagnement | null) ?? '',
      accompagnementDetails: inclusion.staff_details ?? '',
      strategies: inclusion.strategies ?? '',
      materiel: inclusion.assistive_devices ?? '',
      signauxDeStress: inclusion.stress_signals ?? '',
      forces: inclusion.strengths ?? '',
      experiences: inclusion.previous_experience ?? '',
    },
    sorties: {
      partSeul: sorties.leaves_alone ?? false,
      heureDepart: sorties.departure_time ?? '',
      personnesIds: sorties.pickup_people_ids ?? [],
    },
    autorisations: {
      soins: sante.parental_consent ?? false,
      image: depuisLigne(l.image_rights),
      /*
        La lecture de la politique n'a pas de colonne à elle, et n'en a pas
        besoin : la ligne `kid_image_rights` n'est écrite qu'au moment où le
        parent coche la case, et elle porte la version qu'il a vue. Son
        existence EST la trace — plus fiable qu'un booléen, puisqu'elle dit
        aussi de quel texte il s'agissait.
      */
      politiqueLue: !!l.image_rights?.policy_version,
    },
  };
}
